import useChatSummariesReconciliation from 'hooks/chat/useChatSummariesReconciliation';
import useCompletedChatMessage from 'hooks/chat/useCompletedChatMessage';
import { useCallback, useEffect, useState } from 'react';
import chatService from 'services/chat';
import { buildAssignmentChatFilter } from 'utils/chat';

/**
 * Summary-level chat loader for a single assignment.
 *
 * Use this when a page needs the chat metadata/latest message, not the full message thread.
 * It owns initial summary loading, completion refreshes, and websocket drift reconciliation.
 * Pair it with `useAssignmentChatThread` only when the user opens the full chat panel.
 *
 * @param {Object} props
 * @param {string} props.assignmentId Assignment whose chat summary should be loaded.
 * @param {string} props.courseId Course that owns the assignment.
 * @param {string} props.ownerAccountId Account that owns the chat.
 * @param {boolean} [props.reconciliationEnabled=true] Whether websocket drift checks should refresh the summary.
 * @returns {{assignmentChat: Object|null, setAssignmentChat: Function}} Current summary and an override setter.
 */
const useAssignmentChat = ({ assignmentId, courseId, ownerAccountId, reconciliationEnabled = true }) => {
    const [assignmentChat, setAssignmentChat] = useState(null);
    const canLoadAssignmentChat = Boolean(assignmentId && courseId && ownerAccountId);

    const loadAssignmentChat = useCallback(async () => {
        if (!canLoadAssignmentChat) return null;

        return chatService.getAssignmentChatSummary(
            buildAssignmentChatFilter({
                assignmentId,
                courseId,
                ownerAccountId,
            })
        );
    }, [assignmentId, canLoadAssignmentChat, courseId, ownerAccountId]);

    const refreshAssignmentChat = useCallback(async () => {
        const nextAssignmentChat = await loadAssignmentChat();

        setAssignmentChat(nextAssignmentChat);
        return nextAssignmentChat;
    }, [loadAssignmentChat]);

    useEffect(() => {
        if (!canLoadAssignmentChat) {
            setAssignmentChat(null);
            return;
        }

        let isMounted = true;

        const loadInitialAssignmentChat = async () => {
            try {
                const nextAssignmentChat = await loadAssignmentChat();
                if (!isMounted) return;

                setAssignmentChat(nextAssignmentChat);
            } catch (error) {
                if (!isMounted) return;
                console.error(error);
            }
        };

        loadInitialAssignmentChat();

        return () => {
            isMounted = false;
        };
    }, [canLoadAssignmentChat, loadAssignmentChat]);

    useCompletedChatMessage(async ({ assignment_id: completedAssignmentId }) => {
        if (completedAssignmentId !== assignmentId) return;
        await refreshAssignmentChat();
    });

    useChatSummariesReconciliation({
        chats: assignmentChat ? [assignmentChat] : [],
        enabled: Boolean(assignmentChat?.id) && reconciliationEnabled,
        onOutOfSync: async (chatId) => {
            if (chatId !== assignmentChat?.id) return;
            await refreshAssignmentChat();
        },
    });

    return {
        assignmentChat,
        setAssignmentChat,
    };
};

export default useAssignmentChat;
